import { Section, H2, P, Bullets, Table, Callout, Figure } from "./Primitives";
import { LineChart, Legend } from "./Charts";
import { eur, OFFER, B2B_SCENARIOS, cashflow, paybackMonth, partnerCost, breakEvenSales, MONTHS } from "../../data/model";

const PriceCard = ({ opt, title, tag, lines, testId, featured }) => {
  const o = OFFER[opt];
  return (
    <div data-testid={testId} className={`price-card avoid-break ${featured ? "price-card--featured" : ""}`}>
      <div className="kicker mb-1">{tag}</div>
      <h3 className="font-display text-2xl text-navy mb-5">{title}</h3>
      <div className="flex items-end gap-3">
        <span className="font-display text-4xl leading-none text-navy" data-testid={`${testId}-setup`}>{eur(o.setup)}</span>
        <span className="strike text-[var(--ink-soft)]">{eur(o.listSetup)}<i className="strike-line" /></span>
      </div>
      <div className="text-xs uppercase tracking-[0.14em] text-[var(--ink-soft)] mt-3 mb-5">Setup una tantum · IVA esclusa</div>
      <div className="flex items-end gap-3">
        <span className="font-display text-3xl leading-none text-navy" data-testid={`${testId}-fee`}>{eur(o.fee)}<span className="text-base">/mese</span></span>
        <span className="strike text-[var(--ink-soft)]">{eur(o.listFee)}<i className="strike-line" /></span>
      </div>
      <div className="text-xs uppercase tracking-[0.14em] text-[var(--ink-soft)] mt-3 mb-5">Piattaforma, server e manutenzione</div>
      <div className="h-px bg-[rgba(176,141,87,0.5)] mb-5" />
      <Bullets items={lines} />
    </div>
  );
};

const Payback = () => {
  const labels = Array.from({ length: 24 }, (_, i) => `${MONTHS[i % 12]} '${i < 12 ? 27 : 28}`);
  const colors = { prudente: "#8A8A8A", medio: "#14213D", aggressivo: "#5B6C8F" };
  const series = B2B_SCENARIOS.map((s) => {
    const v = cashflow(s.y1, s.y2, "A");
    return { name: `Opzione A · ${s.label} (rientro ${paybackMonth(v)}° mese)`, values: v, color: colors[s.key], bold: s.key === "medio", dash: s.key === "medio" ? "" : "5 4", marker: s.key === "medio" ? paybackMonth(v) : null };
  });
  const bm = cashflow(60000, 90000, "B");
  series.push({ name: `Opzione B · Medio (rientro ${paybackMonth(bm)}° mese)`, values: bm, color: "#B08D57", bold: true });
  return (
    <Figure caption="Figura 3 — Flusso cumulato netto dei 24 mesi (mese 1 = gennaio 2027): risparmi e nuovi margini meno setup, canoni, budget media Fasi 1–2 e restyling dei punti vendita.">
      <LineChart testId="chart-payback" series={series} labels={labels} />
      <Legend items={series} />
    </Figure>
  );
};

const AvsB = () => {
  const levels = [
    { l: "Prudente", s12: 40000, s24: 100000 },
    { l: "Medio", s12: 60000, s24: 150000 },
    { l: "Aggressivo", s12: 90000, s24: 220000 },
  ];
  return (
    <Table
      testId="table-a-vs-b"
      head={["Vendite B2B dal portale", "A · 12 mesi", "B · 12 mesi", "A · 24 mesi", "B · 24 mesi"]}
      rows={levels.map((x) => [
        `${x.l}: ${eur(x.s12)} / ${eur(x.s24)}`,
        eur(partnerCost("A", 12, x.s12)),
        eur(partnerCost("B", 12, x.s12)),
        eur(partnerCost("A", 24, x.s24)),
        eur(partnerCost("B", 24, x.s24)),
      ])}
      foot={`Costo cumulato verso PartnerTech: setup, canoni (con 2 mesi gratuiti) e commissioni. Le due opzioni costano uguale con ${eur(breakEvenSales(12))} di vendite B2B nei primi 12 mesi o con ${eur(breakEvenSales(24))} cumulati in 24 mesi.`}
    />
  );
};

const FounderOffer = () => (
  <>
  <H2>Proposta riservata — Sconto “Partner Fondatore”</H2>
  <P>
    Bella Napoli sarebbe il <strong>primo cliente della rete Synapse OS</strong> nel settore pasticceria.
    In virtù di questo ruolo e del rapporto di fiducia con PartnerTech, riserviamo condizioni non replicabili per i clienti successivi.
  </P>
  <div className="grid md:grid-cols-2 gap-6 my-8">
    <PriceCard
      testId="price-card-a" opt="A" featured tag="Opzione A" title="Chiavi in mano"
      lines={[
        <><strong>–18% sul setup</strong> (listino € 14.500) e canone ridotto da € 850 a € 690.</>,
        <>Il <strong>100% degli utili resta a Bella Napoli</strong>: nessuna commissione sulle vendite.</>,
        <>Gestionale, App Clienti e Portale B2B inclusi.</>,
        <>Server, aggiornamenti, assistenza e manutenzione inclusi nel canone.</>,
      ]}
    />
    <PriceCard
      testId="price-card-b" opt="B" tag="Opzione B" title="Partner strategico a performance"
      lines={[
        <>Setup ridotto a € 3.900 (listino € 4.800) e canone da € 450 a € 390.</>,
        <><strong>Commissione del 10%</strong> (listino 12%), applicata solo alle vendite B2B generate dal portale.</>,
        <>PartnerTech condivide il rischio: guadagna solo se il portale vende.</>,
        <>Ideale per contenere l'esborso iniziale.</>,
      ]}
    />
  </div>
  <Table
    testId="table-pricing"
    head={["", "Listino", "Proposta Bella Napoli"]}
    rows={[
      ["Opzione A · Chiavi in mano", "€ 14.500 + € 850/mese", "€ 11.900 una tantum (–18%) + € 690/mese"],
      ["Opzione B · Partner a performance", "€ 4.800 + € 450/mese + 12%", "€ 3.900 una tantum + € 390/mese + 10% sulle vendite B2B del portale"],
      ["Clausola Franchising (comune)", "3% sulla fee d'ingresso", "Invariata: 3% sulla fee d'ingresso di ogni nuovo affiliato"],
    ]}
  />

  </>
);

const PaymentTerms = () => (
  <>
  <H2>Condizioni che abbassano la soglia d'ingresso</H2>
  <Bullets
    items={[
      <><strong>Setup in 3 tranche</strong>: 40% alla firma, 30% al collaudo e 30% al lancio dell'App. Con l'Opzione A significa <strong>€ 4.760 alla firma</strong>.</>,
      <><strong>Primi 2 mesi di canone gratuiti</strong> durante l'avvio: si paga solo quando il sistema è operativo.</>,
      <><strong>Prezzo bloccato per 24 mesi</strong>: nessun adeguamento del canone nel biennio.</>,
      <>In 24 mesi l'Opzione A costa {eur(partnerCost("A", 24, 0))} contro {eur(14500 + 850 * 24)} a listino: un <strong>risparmio di {eur(14500 + 850 * 24 - partnerCost("A", 24, 0))}</strong>.</>,
    ]}
  />

  </>
);

const PaybackBlock = () => (
  <>
  <H2>Rientro dell'investimento</H2>
  <P>
    Il calcolo usa ipotesi <strong>volutamente prudenti</strong>. La prima è la riduzione degli sprechi del 3% sul costo delle materie prime, pari a circa € 2.600 al mese.
    Seguono 3 ore amministrative risparmiate a settimana per sede (circa € 1.820 al mese) e un margine del 38% sulla linea B2B.
    L'ultima è l'incremento del 3% dello scontrino medio nei 3 punti vendita Luxury, pari a circa € 1.860 al mese. Gli investimenti considerati sono setup, canoni, budget media delle Fasi 1–2 e restyling dei punti vendita.
  </P>
  <Payback />

  </>
);

const OptionCompare = () => (
  <>
  <H2>Opzione A o Opzione B? Il confronto razionale</H2>
  <AvsB />
  <Callout title="Raccomandazione">
    Se la Proprietà prevede vendite B2B dal portale <strong>inferiori a circa {eur(breakEvenSales(24))} nei primi due anni</strong>, l'Opzione B è più conveniente e riduce l'esborso iniziale.
    Se crede nel potenziale della linea strenne oltre questa soglia, l'Opzione A massimizza il margine: <strong>il 100% degli utili resta in azienda</strong>.
  </Callout>

  </>
);

const FranchiseClause = () => (
  <>
  <H2>Clausola Franchising (comune a entrambe le opzioni)</H2>
  <P>
    A ogni nuovo affiliato viene concesso in uso il gestionale Synapse OS, così che il punto vendita sia operativo e allineato agli standard del marchio fin dal primo giorno.
    A fronte di questa concessione, PartnerTech percepisce il <strong>3% sulla fee d'ingresso</strong> di ogni futuro affiliato.
    Con una fee di € 12.000 corrispondono € 360 per apertura, una quota marginale rispetto al valore di un affiliato già dotato di gestionale, App e portale.
  </P>
  </>
);

export const Proposal = () => (
  <Section id="proposta" num="06" kicker="Proposta Commerciale" title="Opzioni di investimento PartnerTech">
    <H2>Il valore di mercato della soluzione</H2>
    <P>Per realizzare da zero lo stesso ecosistema, un'agenzia di sviluppo italiana chiederebbe oggi questi importi:</P>
    <Table
      testId="table-market-value"
      head={["Componente", "Costo di mercato (realizzazione su misura)"]}
      rows={[
        ["Gestionale multi-sede (laboratorio, magazzino, cassa, reportistica)", "€ 15.000 – 35.000"],
        ["Portale B2B con configuratore e preventivo istantaneo", "€ 8.000 – 15.000"],
        ["App Clienti: versione web e app iOS/Android", "€ 25.000 – 45.000"],
        { strong: true, cells: ["Totale indicativo", "€ 50.000 – 90.000"] },
      ]}
    />
    <Callout title="Perché la proposta costa molto meno">
      <strong>Synapse OS esiste già ed è collaudato.</strong> Il gestionale è già sviluppato: Bella Napoli paga la <strong>configurazione e il collegamento</strong> dei propri punti vendita, non la costruzione.
      Ne derivano un rischio di progetto basso, tempi di avvio brevi e un costo pari a una frazione del valore di mercato. Il setup comprende anche l'App web e per telefono, che si collegano al sistema esistente.
    </Callout>
    <P>
      <strong>Confronto con il canone di mercato.</strong> Oggi una pasticceria spende circa <strong>€ 60 al mese per punto vendita</strong> per una cassa in cloud.
      A questa vanno aggiunti fidelity, e-commerce e sincronizzatori, ciascuno a parte. Per 7 sedi si arriva a <strong>€ 700–1.000 al mese</strong>, senza un'App proprietaria e senza un portale B2B.
      Il canone proposto di € 690 al mese, pari a circa <strong>€ 99 per punto vendita tutto incluso</strong>, comprende invece l'intero ecosistema.
    </P>

    <FounderOffer />
    <PaymentTerms />
    <PaybackBlock />
    <OptionCompare />
    <FranchiseClause />
  </Section>
);
